import { Component, OnInit, OnDestroy, ElementRef } from "@angular/core";
import { Contract } from "@edp-components/edp-contract";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { ActiveMeters } from "./edp-consumptions.models";
import { getActiveMetersElectricity, getActiveMetersGas } from "@edp-store/selectors/dashboard.selectors";
import { NgbNavChangeEvent, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { GetActiveMeters, SetUploadReading, UploadReading } from "@edp-store/actions/dashboard.actions";
import { getContract, getContractDetails } from "@edp-store/selectors/contract.selectors";
import { combineLatest, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EdpModalService } from "@edp-components/edp-modal";
import { EdpWidgetConsumptionsSupportComponent } from "./edp-widget-consumptions-support/edp-widget-consumptions-support.component";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { environment } from "@edp-environments/environment";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { TranslateService } from "@ngx-translate/core";
import { ListItem } from "@edp-models/lists.model";

@Component({
    selector: "edp-widget-consumptions",
    templateUrl: "./edp-widget-consumptions.component.html",
    styleUrls: ["./edp-widget-consumptions.component.scss"],
})
export class EdpContractConsumptionsComponent implements OnInit, OnDestroy {
    public activeTab: number;
    public activeMetersElectricity: ActiveMeters[];
    public activeMetersGas: ActiveMeters[];
    public changeMobile = false;
    public contract: Contract = null;
    public simulateElectricity = false;
    public simulateGas = false;
    public isApp = false;
    public updateElectricityActiveMetersFromSenv = true;
    public updateGasActiveMetersFromSenv = true;
    public updateActiveMetersFromSenv = true;
    public showTabElectricityActive = true;
    public unsubscribe = new Subject();
    private modalOptions: NgbModalOptions = {
        windowClass: "edp-dialog-fullscreen",
    };
    private modalData: IModalData = {
        title: "",
        showClose: true,
    };

    public selectedElectricityMeterId: ListItem = null;
    public selectedElectricityMeter: ActiveMeters = null;
    public activeMetersElectricityId: ListItem[] = [];

    public selectedGasMeterId: ListItem = null;
    public activeMetersGasId: ListItem[] = [];
    public selectedGasMeter: ActiveMeters = null;
    public labelElectricity: string;

    public isMultimeters: boolean;
    public isEletricityMultiMeter: boolean;
    public isGasMultiMeter: boolean;
    private executeGetMetersEletricityOnDestroy = false;
    private executeGetMetersGasOnDestroy = false;

    constructor(
        public store: Store<IAppState>,
        private edpModalService: EdpModalService,
        private elementRef: ElementRef<HTMLElement>,
        private toaster: EdpToasterService,
        public translate: TranslateService
    ) {
        this.isApp = environment.isApp;
    }

    public ngOnInit() {
        combineLatest([
            this.store.pipe(takeUntil(this.unsubscribe), select(getContract)),
            this.store.pipe(takeUntil(this.unsubscribe), select(getContractDetails)),
        ]).subscribe(([contract, detail]) => {
            if (!!contract && !!detail) {
                this.simulateElectricity = false;
                this.simulateGas = false;
                this.activeMetersElectricity = null;
                this.activeMetersGas = null;

                this.contract = contract;
                this.getMeters(this.contract.HasE, this.contract.HasG);

                this.showTabElectricityActive = !detail.getSelfConsumption() && this.contract.HasE;

                const type = contract.getType();
                if (this.showTabElectricityActive) {
                    this.activeTab = 1;
                } else if (type === "gas") {
                    this.activeTab = 2;
                }
            }
        });

        this.focusWidget();
        this.labelElectricity = this.translate.instant("pt.edp.edponline.filter.picker.counter");
    }

    private getMeters(executeElecrticity: boolean, executeGas: boolean) {
        const isDual = executeElecrticity && executeGas;
        if (isDual) {
            this.getMetersElectricity(true);
            return;
        }
        if (executeElecrticity) {
            this.getMetersElectricity(false);
            return;
        }
        if (executeGas) {
            this.getMetersGas(true);
            return;
        }
    }

    private getMetersElectricity(executeGasAfterEletricity = false): void {
        this.store.pipe(takeUntil(this.unsubscribe), select(getActiveMetersElectricity)).subscribe((meters: ActiveMeters[]) => {
            if (!meters || this.executeGetMetersEletricityOnDestroy) {
                this.store.dispatch(new GetActiveMeters(this.contract, "E", false));
                this.executeGetMetersEletricityOnDestroy = false;
                return;
            }

            if ((!!meters && !!executeGasAfterEletricity) || this.executeGetMetersGasOnDestroy) {
                this.getMetersGas(false);
            }
            // This implementation is here to not loose the form state
            if (meters && JSON.stringify(this.activeMetersElectricity) !== JSON.stringify(meters)) {
                this.activeMetersElectricityId = [];
                this.selectedElectricityMeterId = null;
                this.activeMetersElectricity = meters;
                this.selectedElectricityMeter = meters[0];

                this.getActiveMetersElectricityIds();

                this.isEletricityMultiMeter = this.activeMetersElectricity.length > 1;
                return;
            }
        });
    }

    private getMetersGas(considerFromSenv = true, reload = false): void {
        this.store.pipe(takeUntil(this.unsubscribe), select(getActiveMetersGas)).subscribe((meters: ActiveMeters[]) => {
            if (!meters || this.executeGetMetersGasOnDestroy) {
                this.store.dispatch(new GetActiveMeters(this.contract, "G", false));
                this.executeGetMetersGasOnDestroy = false;
                return;
            }

            // This implementation is here to not loose the form state
            if (meters && JSON.stringify(this.activeMetersGas) !== JSON.stringify(meters)) {
                this.activeMetersGasId = [];
                this.selectedGasMeterId = null;
                this.activeMetersGas = meters;
                this.selectedGasMeter = meters[0];

                this.getActiveMetersGasIds();

                this.isGasMultiMeter = this.activeMetersGas.length > 1;

                if (this.updateGasActiveMetersFromSenv && considerFromSenv) {
                    this.updateGasActiveMetersFromSenv = false;
                    this.store.dispatch(new GetActiveMeters(this.contract, "G", false));
                }
            }
        });
    }

    private focusWidget() {
        if (history.state && history.state.element && history.state.element === "contractConsumptions") {
            this.store.dispatch(new GetActiveMeters(this.contract, history.state.extra));
            setTimeout(() => {
                this.elementRef.nativeElement.scrollIntoView({ behavior: "smooth", block: "center" });

                this.toggleWidget(history.state.extra === "G" ? 2 : 1);
            }, 1000);
        }
    }

    public onNavChange(changeEvent: NgbNavChangeEvent) {
        if (changeEvent.nextId === 2 && !this.activeMetersGas) {
            this.store.dispatch(new GetActiveMeters(this.contract, "G"));
        }
    }

    public toggleWidget(activeId: number): void {
        this.changeMobile = !this.changeMobile;
        if (this.changeMobile) {
            this.activeTab = activeId;
        }
    }

    public onSubmitted(event: any) {
        let meter = null;
        if (event.SupplyType === "E") {
            meter = this.selectedElectricityMeterId.value;
            this.isMultimeters = this.activeMetersElectricity.length > 1;
            this.simulateElectricity = true;
        } else if (event.SupplyType === "G") {
            this.isMultimeters = this.activeMetersGas.length > 1;
            this.simulateGas = true;
            meter = this.selectedGasMeterId.value;
        }
        this.store.dispatch(new UploadReading({ ...event, ...{ Meter: meter } }));
    }

    public reloadWidget(supplyType: string) {
        this.store.dispatch(new SetUploadReading(null));
        if (supplyType === "E") {
            this.simulateElectricity = false;
        } else {
            this.simulateGas = false;
        }
        this.toaster.create(this.translate.instant("pt.edp.edponline.dashboard.widget.consumptions.try.again"), EdpToastType.info).show();
        this.store.dispatch(new GetActiveMeters(this.contract, supplyType));
    }

    showHowReadMeter() {
        this.edpModalService
            .custom(EdpWidgetConsumptionsSupportComponent, this.modalOptions, this.modalData)
            .then(readingResultArgs => {
                if (readingResultArgs) {
                    this.onSubmitted(readingResultArgs);
                }
            })
            .catch(() => {
                /* Intentional Silent Catch */
            });
    }
    private getActiveMetersElectricityIds() {
        this.activeMetersElectricity.forEach(el =>
            this.activeMetersElectricityId.push({
                value: el.idMeter,
                label: el.idMeter,
            })
        );

        this.selectedElectricityMeterId = this.activeMetersElectricityId[0];
    }

    private getActiveMetersGasIds() {
        this.activeMetersGas.forEach(el =>
            this.activeMetersGasId.push({
                value: el.idMeter,
                label: el.idMeter,
            })
        );

        this.selectedGasMeterId = this.activeMetersGasId[0];
    }

    public setActiveElectrivityMeter(event) {
        this.selectedElectricityMeterId = event;
        this.selectedElectricityMeter = this.activeMetersElectricity.find(el => el.idMeter === event.value);
    }

    public setActiveGasMeter(event) {
        this.selectedGasMeterId = event;
        this.selectedGasMeter = this.activeMetersGas.find(el => el.idMeter === event.value);
    }

    public executeGetMetersOnDestroy(event: { type: "E" | "G" }) {
        if (event.type === "E") {
            this.executeGetMetersEletricityOnDestroy = true;
        }
        if (event.type === "G") {
            this.executeGetMetersGasOnDestroy = true;
        }
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.getMeters(this.executeGetMetersEletricityOnDestroy, this.executeGetMetersGasOnDestroy);
    }

}
